import { DatePickerProps } from '@progress/kendo-react-dateinputs';
import { Hint, Label } from '@progress/kendo-react-labels';
import { IconButton } from 'app/components/buttons';
import {
  AlternateSelect,
  SelectOption,
} from 'app/components/form-fields/AlternateSelect';
import { TextField } from 'app/components/form-fields/TextField';
import { ChevronRight, ClockIcon, CloseIcon } from 'app/components/icons';
import { useIsMobile } from 'app/hooks/useIsMobile';
import clsx from 'clsx';
import { format } from 'date-fns';
import fr from 'date-fns/locale/fr';
import { getLocale } from 'locales/utils';
import moment from 'moment';
import React, { useCallback, useMemo } from 'react';
import DatePicker, {
  ReactDatePickerCustomHeaderProps,
  registerLocale,
} from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import './DateTimePicker.less';
import { months } from './constants';

registerLocale('fr', fr as any);

type Props = Pick<
  DatePickerProps,
  'className' | 'label' | 'value' | 'max' | 'min'
> & {
  helperText?: string;
  dateFormat?: string;
  timeFormat?: string;
  datePlaceholder?: string;
  timePlaceholder?: string;
  clearable?: boolean;
  timeDisabled?: boolean;
  selectsStart?: boolean;
  selectsEnd?: boolean;
  startDate?: Date | null;
  endDate?: Date | null;
  onChange: (date: Date | null) => void;
};

export default function DateTimePicker(props: Props) {
  const {
    label,
    helperText,
    className,
    dateFormat,
    timeFormat,
    datePlaceholder,
    timePlaceholder,
    value,
    timeDisabled,
    max,
    min,
    clearable,
    selectsStart,
    selectsEnd,
    startDate,
    endDate,
    onChange,
  } = props;

  const isMobile = useIsMobile();

  const locale = getLocale();
  const isFrenchLocale = locale.includes('fr');

  const { t } = useTranslation();

  const options = useMemo<SelectOption<number>[]>(
    () =>
      months.map((month, index) => ({
        label: t(`calendar.months.${month}`),
        value: index,
      })),
    [t],
  );

  const renderCustomHeader = useCallback(
    ({
      date,
      decreaseMonth,
      increaseMonth,
      changeMonth,
    }: ReactDatePickerCustomHeaderProps) => {
      const formattedDate = format(date, 'MMMM yyyy', {
        locale: isFrenchLocale ? (fr as any) : undefined,
      });

      return (
        <>
          <div className="date-time-picker__custom-header-container">
            <IconButton unstyled onClick={decreaseMonth}>
              <ChevronRight className="date-time-picker__chevron-left" />
            </IconButton>

            <AlternateSelect
              label={
                <h3 className="date-time-picker__custom-header-btn">
                  {formattedDate}
                </h3>
              }
              value={moment(date).month()}
              options={options}
              onChange={changeMonth}
            />

            <IconButton unstyled onClick={increaseMonth}>
              <ChevronRight />
            </IconButton>
          </div>
        </>
      );
    },
    [],
  );

  function CustomDatePickerInput(props: any) {
    return (
      <div className="date-time-picker__custom-input-container">
        <TextField
          {...props}
          value={props.value}
          readOnly
          className={clsx(props.className, 'date-time-picker__custom-input')}
        />

        {value && clearable && (
          <IconButton
            unstyled
            className="date-time-picker__icon"
            onClick={() => onChange(null)}
          >
            <CloseIcon height={24} width={24} stroke="#909090" />
          </IconButton>
        )}
      </div>
    );
  }

  function CustomTimePickerInput(props: any) {
    return (
      <div className="date-time-picker__custom-input-container">
        <TextField
          {...props}
          readOnly
          className={clsx(props.className, 'date-time-picker__custom-input')}
        />

        <ClockIcon className="date-time-picker__icon" />
      </div>
    );
  }

  return (
    <div className={clsx('date-time-picker', className)}>
      {label && <Label className="date-time-picker__label">{label}</Label>}

      <div className="date-time-picker__controls">
        <DatePicker
          wrapperClassName={'date-time-picker__date-control'}
          portalId="date-picker-portal"
          withPortal={isMobile}
          selected={value}
          onChange={onChange}
          placeholderText={datePlaceholder}
          dateFormat={dateFormat}
          renderCustomHeader={renderCustomHeader}
          minDate={min}
          maxDate={max}
          formatWeekDay={date => date[0].toUpperCase() + date[1]}
          disabledKeyboardNavigation
          customInput={<CustomDatePickerInput />}
          locale={isFrenchLocale ? 'fr' : 'en'}
          fixedHeight
          selectsStart={selectsStart}
          selectsEnd={selectsEnd}
          startDate={startDate}
          endDate={endDate}
          selectsDisabledDaysInRange
        />

        <DatePicker
          wrapperClassName="date-time-picker__time-control"
          portalId="time-picker-portal"
          withPortal={isMobile}
          selected={value}
          onChange={onChange}
          placeholderText={timePlaceholder}
          dateFormat={timeFormat}
          timeFormat={timeFormat}
          showTimeSelect
          showTimeSelectOnly
          timeIntervals={30}
          customInput={<CustomTimePickerInput />}
          disabled={timeDisabled}
          disabledKeyboardNavigation
          showTimeCaption={false}
        />
      </div>

      {helperText && (
        <Hint className="date-time-picker__helper-text">{helperText}</Hint>
      )}
    </div>
  );
}
